import type React from 'react';

import { useReactFlow } from '@xyflow/react';

import { flowchartLogger } from 'src/lib/logger';

import AiAssistantControls from './AiAssistant/AiAssistantControls';
import AiVoiceAnalysisControls from './AiVoiceAnalysis/AiVoiceAnalysisControls';
import AnswerControls from './Answer/AnswerControls';
import CalendarControls from './Calendar/CalendarControls';
import ConferenceControls from './Conference/ConferenceControls';
import DisconnectControls from './Disconnect/DisconnectControls';
import DistributionListControls from './DistributionList/DistributionListControls';
import ExtensionControls from './Extension/ExtensionControls';
import ForwardControls from './Forward/ForwardControls';
import GotoControls from './Goto/GotoControls';
import GroupControls from './Group/GroupControls';
import HTTPControls from './HTTP/HTTPControls';
import HuntControls from './Hunt/HuntControls';
import IVRControls from './IVR/IVRControls';
import LCRControls from './LCR/LCRControls';
import MappingControls from './Mapping/MappingControls';
import PlaybackControls from './Playback/PlaybackControls';
import QueueControls from './Queue/QueueControls';
import RouterControls from './Router/RouterControls';
import SpeechControls from './Speech/SpeechControls';
import UserControls from './User/UserControls';
import UserVoicemailControls from './UserVoicemail/UserVoicemailControls';

import type { Node } from '@xyflow/react';
import type { RaRecord } from 'react-admin';

interface NodeControlsProps {
  activeNode: Node;
  /** Record should be passed as a prop as we may have no access to contexts in the settings drawer */
  record: RaRecord | undefined;
  /** Resource should be passed as a prop as we may have no access to contexts in the settings drawer */
  resource: string;
}

const nodeTypeToComponent: Record<string, React.ElementType> = {
  ai_assistant: AiAssistantControls,
  ai_voice_analysis: AiVoiceAnalysisControls,
  answer: AnswerControls,
  calendar: CalendarControls,
  conference: ConferenceControls,
  disconnect: DisconnectControls,
  distribution_list: DistributionListControls,
  extension: ExtensionControls,
  forward: ForwardControls,
  goto: GotoControls,
  groupNode: GroupControls,
  http: HTTPControls,
  hunt: HuntControls,
  ivr: IVRControls,
  lcr: LCRControls,
  mapping: MappingControls,
  playback: PlaybackControls,
  queue: QueueControls,
  router: RouterControls,
  speech: SpeechControls,
  user: UserControls,
  user_voicemail: UserVoicemailControls,
};

export function getControlsComponentName(nodeType: string): React.ElementType | false {
  return nodeTypeToComponent[nodeType] ?? false;
}

export const prepareUpdateData = (activeNode: Node, data: {}, nodes: Node[]): Node[] | false => {
  const node = nodes.find((n: Node) => n.id === activeNode.id);
  flowchartLogger.debug('NodeControls: updateNodeData', { data, node });
  if (node !== undefined) {
    node.data = {
      ...node.data,
      // Drop server-side validation markers after a local edit.
      serverErrors: undefined,
      ...data,
    };
    return [...nodes];
  } else {
    return false;
  }
};

export default function NodeControls({ activeNode, record, resource }: NodeControlsProps): React.ReactElement | null {
  const reactFlowInstance = useReactFlow();

  const updateNodeData = (data: {}): void => {
    const nodes = reactFlowInstance.getNodes();
    const updatedNodes = prepareUpdateData(activeNode, data, nodes);
    if (updatedNodes !== false) {
      reactFlowInstance.setNodes([...nodes]);
    }
  };

  if (activeNode?.type === undefined) {
    flowchartLogger.debug('NodeControls: activeNode.type is undefined');
    return null;
  }

  // Not using the getControlsComponentName function to avoid component creation during render
  const ControlsComponent = nodeTypeToComponent[activeNode.type];
  if (ControlsComponent !== undefined) {
    return (
      <ControlsComponent
        updateNodeData={updateNodeData}
        data={{ ...activeNode.data }}
        record={record}
        resource={resource}
      />
    );
  }
  return null;
}
